import { TargetedSetupError } from "./targeted-setup-service";
import {
  authenticationResponseSchema,
  registrationResponseSchema,
} from "./webauthn-request-schemas";
import type {
  PasskeyRegistrationUser,
  PasskeyService,
  WebAuthnRequestContext,
} from "./passkey-service";
import type { AuthSessionPersistence } from "./session-store";
import type { ResolvedSetupToken, SetupFlow } from "./setup-flow";
import { issuerFromRequest, isSecureRequest } from "./issuer";
import { jsonResponse, oauthErrorResponse } from "./http-responses";

/** The passkey operations these endpoints drive. */
export type EndpointPasskeys = Pick<
  PasskeyService,
  | "hasCredentials"
  | "generateRegistrationOptions"
  | "verifyRegistrationResponse"
  | "generateAuthenticationOptions"
  | "verifyAuthenticationResponse"
>;

/** The one session operation these endpoints perform. */
export type EndpointSessions = Pick<AuthSessionPersistence, "createSession">;

/** The setup-token operations these endpoints perform. */
export type EndpointSetupFlow = Pick<
  SetupFlow,
  "resolveSetupToken" | "hasConflictingAccountSession" | "consumeSetupToken"
>;

export interface WebAuthnEndpointsOptions {
  passkeyService: EndpointPasskeys;
  sessionStore: EndpointSessions;
  setupFlow: EndpointSetupFlow;
  registrationUserProvider: (
    userId?: string,
  ) => Promise<PasskeyRegistrationUser>;
  validateTargetedRegistration?: (
    setup: ResolvedSetupToken & { targetUserId: string },
  ) => Promise<void>;
  completeTargetedRegistration?: (
    setup: ResolvedSetupToken & { targetUserId: string },
  ) => Promise<void>;
  recordAuditEvent?: (event: {
    action: string;
    targetType?: string;
    targetId?: string;
    metadata?: Record<string, unknown>;
  }) => Promise<void>;
}

/**
 * WebAuthn HTTP endpoints: passkey registration (gated by the one-shot
 * setup token) and passkey authentication, both ending in an authenticated
 * session cookie.
 */
export class WebAuthnEndpoints {
  private readonly passkeyService: EndpointPasskeys;
  private readonly sessionStore: EndpointSessions;
  private readonly setupFlow: EndpointSetupFlow;
  private readonly registrationUserProvider: (
    userId?: string,
  ) => Promise<PasskeyRegistrationUser>;
  private readonly validateTargetedRegistration:
    | ((setup: ResolvedSetupToken & { targetUserId: string }) => Promise<void>)
    | undefined;
  private readonly completeTargetedRegistration:
    | ((setup: ResolvedSetupToken & { targetUserId: string }) => Promise<void>)
    | undefined;
  private readonly recordAuditEvent:
    | ((event: {
        action: string;
        targetType?: string;
        targetId?: string;
        metadata?: Record<string, unknown>;
      }) => Promise<void>)
    | undefined;

  constructor(options: WebAuthnEndpointsOptions) {
    this.passkeyService = options.passkeyService;
    this.sessionStore = options.sessionStore;
    this.setupFlow = options.setupFlow;
    this.registrationUserProvider = options.registrationUserProvider;
    this.validateTargetedRegistration = options.validateTargetedRegistration;
    this.completeTargetedRegistration = options.completeTargetedRegistration;
    this.recordAuditEvent = options.recordAuditEvent;
  }

  async handleRegistrationOptions(request: Request): Promise<Response> {
    const setup = await this.resolveRegistrationSetup(request);
    if (setup instanceof Response) return setup;

    try {
      const options = await this.passkeyService.generateRegistrationOptions(
        webAuthnRequestContext(request),
        await this.registrationUserProvider(setup.targetUserId ?? undefined),
      );
      return jsonResponse(options);
    } catch {
      return oauthErrorResponse(
        "access_denied",
        "Passkey registration user is unavailable",
      );
    }
  }

  async handleRegistrationVerify(request: Request): Promise<Response> {
    const setup = await this.resolveRegistrationSetup(request);
    if (setup instanceof Response) return setup;

    if (setup.targetUserId) {
      try {
        await this.registrationUserProvider(setup.targetUserId);
        await this.validateTargetedRegistration?.({
          ...setup,
          targetUserId: setup.targetUserId,
        });
      } catch (error) {
        await this.recordRegistrationFailure(setup.targetUserId);
        return oauthErrorResponse(
          "access_denied",
          targetedSetupFailureDescription(error),
        );
      }
    }

    // A malformed credential body is a failed registration, not a crash: it
    // gets the same response the downstream verification failure produces.
    const credential = registrationResponseSchema.safeParse(
      await request.json(),
    );
    if (!credential.success) {
      await this.recordRegistrationFailure(setup.targetUserId);
      return oauthErrorResponse("access_denied", "Passkey registration failed");
    }

    const result = await this.passkeyService.verifyRegistrationResponse(
      credential.data,
      webAuthnRequestContext(request),
      setup.targetUserId ?? undefined,
    );
    if (
      !result.verified ||
      (setup.targetUserId !== null && result.subject !== setup.targetUserId)
    ) {
      await this.recordRegistrationFailure(setup.targetUserId);
      return oauthErrorResponse("access_denied", "Passkey registration failed");
    }

    if (setup.targetUserId && this.completeTargetedRegistration) {
      try {
        await this.completeTargetedRegistration({
          ...setup,
          targetUserId: setup.targetUserId,
        });
      } catch (error) {
        await this.recordRegistrationFailure(setup.targetUserId);
        return oauthErrorResponse(
          "access_denied",
          targetedSetupFailureDescription(error),
        );
      }
    } else {
      await this.setupFlow.consumeSetupToken(setup.token);
    }
    const session = await this.sessionStore.createSession(result.subject, {
      secure: isSecureRequest(request),
    });
    return jsonResponse({ verified: true }, 200, {
      "Set-Cookie": session.cookie,
    });
  }

  /**
   * The setup gate both registration endpoints sit behind: a setup token is
   * required, and once a passkey exists only a targeted token can add one.
   * Returns the resolved setup, or the refusal to send instead.
   */
  private async resolveRegistrationSetup(
    request: Request,
  ): Promise<ResolvedSetupToken | Response> {
    const setup = await this.setupFlow.resolveSetupToken(request);
    if (
      (await this.passkeyService.hasCredentials()) &&
      setup?.targetUserId == null
    ) {
      return oauthErrorResponse(
        "access_denied",
        "Passkey setup already completed",
      );
    }
    if (!setup) {
      return oauthErrorResponse("access_denied", "Invalid setup token");
    }
    if (await this.setupFlow.hasConflictingAccountSession(request, setup)) {
      return oauthErrorResponse(
        "access_denied",
        "Sign out before using a setup link for another account",
      );
    }
    return setup;
  }

  private async recordRegistrationFailure(
    targetUserId: string | null,
  ): Promise<void> {
    await this.recordAuditEvent?.({
      action: "auth.passkey.registration_failed",
      ...(targetUserId ? { targetType: "user", targetId: targetUserId } : {}),
    });
  }

  async handleAuthenticationOptions(request: Request): Promise<Response> {
    if (!(await this.passkeyService.hasCredentials())) {
      return oauthErrorResponse("access_denied", "No passkey registered");
    }

    const options = await this.passkeyService.generateAuthenticationOptions(
      webAuthnRequestContext(request),
    );
    return jsonResponse(options);
  }

  async handleAuthenticationVerify(request: Request): Promise<Response> {
    // As in registration: a malformed credential body is a failed
    // authentication, reported the same way rather than thrown.
    const credential = authenticationResponseSchema.safeParse(
      await request.json(),
    );
    if (!credential.success) {
      await this.recordAuditEvent?.({
        action: "auth.passkey.authentication_failed",
      });
      return oauthErrorResponse(
        "access_denied",
        "Passkey authentication failed",
      );
    }

    const result = await this.passkeyService.verifyAuthenticationResponse(
      credential.data,
      webAuthnRequestContext(request),
    );
    if (!result.verified) {
      await this.recordAuditEvent?.({
        action: "auth.passkey.authentication_failed",
      });
      return oauthErrorResponse(
        "access_denied",
        "Passkey authentication failed",
      );
    }

    const session = await this.sessionStore.createSession(result.subject, {
      secure: isSecureRequest(request),
    });
    return jsonResponse({ verified: true }, 200, {
      "Set-Cookie": session.cookie,
    });
  }
}

function webAuthnRequestContext(request: Request): WebAuthnRequestContext {
  const issuer = issuerFromRequest(request);
  const origin = new URL(issuer);
  return {
    origin: origin.origin,
    rpID: origin.hostname,
  };
}

const TARGETED_SETUP_FAILURE_DESCRIPTIONS = {
  "token-invalid": "Invalid setup token",
  "user-unavailable": "Passkey registration user is unavailable",
  "delivery-identity-unavailable":
    "This setup link's delivery address is no longer valid; ask an admin for a new link",
  "delivery-unconfirmed":
    "This setup link has not been confirmed as delivered; ask an admin to confirm it",
} as const;

/** What a person holding a targeted setup link is told when it is refused. */
function targetedSetupFailureDescription(error: unknown): string {
  return error instanceof TargetedSetupError
    ? TARGETED_SETUP_FAILURE_DESCRIPTIONS[error.reason]
    : TARGETED_SETUP_FAILURE_DESCRIPTIONS["user-unavailable"];
}
